import { Box, styled } from "@material-ui/core";
import React from "react";
import { graphql, Link } from "gatsby";
import { ReferralSectionFragment as Props } from "./__generated__/ReferralSectionFragment";
import useReferralCredits from "../../hooks/use-referral-credits";
import useReferralSuccessCount from "../../hooks/use-referral-success-count";
import {
  MainTypography,
  ExpressDeliveryButton,
  Wrapper,
  ReferButton,
  PodImage,
  Actions,
  PodCounter
} from "./styles";
import ShareIcon from "../../components/icons/share";
import LocalizedLink from "../../components/localized-link";
import { USER_REWARDS_PATH } from "../../helpers/url";
import useIsAuthenticated from "../../hooks/use-is-authenticated";

const REFFERAL_COUNT_FOR_CREDIT = 3;

function ReferralSection({}: Props) {
  const isAuthenticated = useIsAuthenticated();

  // Credits
  const { data: referralCredits } = useReferralCredits();
  const podCreditsCount =
    referralCredits?.find(c => c.creditType === "GIFT_POD")?.available || 0;
  const hasReferralPodCredits = podCreditsCount > 0;

  // Successfull referral count
  const {
    data: referralSuccessCount,
    isLoading: isLoadingReferralSuccessCount
  } = useReferralSuccessCount();

  // Generate text based on referral success count
  let text = null;

  if (hasReferralPodCredits && podCreditsCount === 1) {
    text = (
      <>
        <strong>Your $1 Reward Pod</strong>
        <>
          {" "}
          is ready to be{" "}
          <span style={{ whiteSpace: "nowrap" }}>redeemed! 🎁</span>
        </>
      </>
    );
  } else if (hasReferralPodCredits && podCreditsCount > 1) {
    text = (
      <>
        <strong>Your $1 Reward Pods</strong>
        <>
          {" "}
          are ready to be{" "}
          <span style={{ whiteSpace: "nowrap" }}>redeemed! 🎁</span>
        </>
      </>
    );
  } else {
    let count = REFFERAL_COUNT_FOR_CREDIT;
    if (isAuthenticated && !isLoadingReferralSuccessCount) {
      count =
        REFFERAL_COUNT_FOR_CREDIT -
        (referralSuccessCount % REFFERAL_COUNT_FOR_CREDIT);
    }
    text = (
      <>
        Refer {count}
        {referralSuccessCount > 0 ? " more" : ""} friends to redeem a{" "}
        <strong>Reward Pod for $1*</strong>
      </>
    );
  }

  return (
    <Wrapper color={hasReferralPodCredits ? "yellow" : "gray"}>
      <PodImage
        src="https://www.datocms-assets.com/15671/1630510342-referral-pod.png"
        width={147 / 2.4}
        height={211 / 2.4}
        disableQualityByDPR
        disableSrcSet
      />
      {podCreditsCount >= 1 && <PodCounter>{podCreditsCount}</PodCounter>}

      <MainTypography>{text}</MainTypography>

      <Actions>
        {/* "Refer friends" button */}
        {!hasReferralPodCredits && (
          <LocalizedLink to={USER_REWARDS_PATH}>
            <ReferButton startIcon={<ShareIcon />}>Refer friends</ReferButton>
          </LocalizedLink>
        )}

        {hasReferralPodCredits && (
          <LocalizedLink to={USER_REWARDS_PATH}>
            <ExpressDeliveryButton>
              See rewards {podCreditsCount > 0 && `(${podCreditsCount})`}
            </ExpressDeliveryButton>
          </LocalizedLink>
        )}
      </Actions>
    </Wrapper>
  );
}

export const ReferralSectionFragment = graphql`
  fragment ReferralSectionFragment on DatoCmsCModuleReferralSection {
    visibilitySettings {
      ...ContentModuleVisibilitySettingFragment
    }
    model {
      apiKey
    }
  }
`;

export default React.memo(ReferralSection);
